/**
 * Prepare the context of new unique event handling
 * @param {string} name The name of the event
 */
function createOnUniqueEvent(name) {
  let currentState = null;

  /**
   * Implement the methods of the unique event interface
   * @param {Object} [options]
   * @param {function(function)} [options.addEventListener]
   * @param {function(function)} [options.removeEventListener]
   * @param {Object} [options.customState]
   * @param {function(any):boolean} [options.customState.isFired]
   * @param {function(any):any} [options.customState.stateInitial]
   * @param {function(any):any} [options.customState.stateFired]
   */
  return function implementUniqueEventInterface({
    addEventListener = (eventHandler) =>
      window.addEventListener(name, eventHandler, { passive: true }),
    removeEventListener = (eventHandler) =>
      window.removeEventListener(name, eventHandler),
    customState: {
      isFired = (state) => state.fired,
      stateFired = (state) => ({ ...state, fired: true }),
      stateInitial = (state) => ({ ...state, fired: false }),
    } = {},
  }) {
    currentState = stateInitial(currentState);

    /** @type {function[]} */
    const listeners = [];

    /**
     * Call the registered listeners (FIFO)
     * @param  {...any} args
     */
    function handleRegisteredListeners(...args) {
      while (listeners.length) {
        const current = listeners.shift();
        // Call the current listener with the arguments passed by the event
        current(...args);
      }
      currentState = stateFired(currentState);
      removeEventListener(handleRegisteredListeners);
    }

    addEventListener(handleRegisteredListeners);

    /**
     * Run a function when the unique event is fired, or run immediately if
     * it is already fired.
     * @param {Function} fn Callback function
     */
    return function onUniqueEvent(listener) {
      if (isFired(currentState)) {
        listener();
      } else {
        listeners.push(listener);
      }
    };
  };
}

module.exports = createOnUniqueEvent;
